import React from "react";
import { Box, Typography } from "@mui/material";

export interface TabLayoutTab {
    label: string;
}

export interface TabLayoutProps {
    /** Step labels in order (e.g. Application Details, Documents Upload, …) */
    tabs: TabLayoutTab[] | string[];
    /** Zero-based index of the active step */
    activeIndex: number;
    /** Called when a step is clicked (optional; omit for display-only) */
    onTabChange?: (index: number) => void;
}

const normalizeTabs = (tabs: TabLayoutTab[] | string[]): TabLayoutTab[] =>
    tabs.map((t) => (typeof t === "string" ? { label: t } : t));

const activeColor = "#00B3B5";
const inactiveColor = "#4A4A4A";
const bottomBorderColor = "rgba(168, 170, 174, 0.4)";

/**
 * Flat, underlined tab strip: active tab shows teal text with a teal
 * underline; inactive tabs are plain gray text. Matches the application
 * flow tabs (Application Details / Documents Upload / Consent & Verification).
 */
const TabLayout: React.FC<TabLayoutProps> = ({
    tabs,
    activeIndex,
    onTabChange,
}) => {
    const items = normalizeTabs(tabs);
    if (items.length === 0) return null;
    const clickable = Boolean(onTabChange);

    return (
        <Box
            sx={{
                boxShadow: "0px 4px 18px 0px #4b465c1a",
                borderRadius: "12px",
                bgcolor: "#fff",
                px: { xs: 1.5, sm: 2.5 },
                pt: { xs: 1, sm: 1.5 },
                maxWidth: "100%",
                boxSizing: "border-box",
            }}
        >
            <Box
                role="tablist"
                aria-label="Application steps"
                sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    alignItems: "flex-end",
                    width: "100%",
                    maxWidth: "100%",
                    borderBottom: `1px solid ${bottomBorderColor}`,
                    columnGap: { xs: 2, sm: 2 },
                    // Desktop tabs fit within the card so no scrollbar appears;
                    // on mobile, where they may not fit, this scrolls horizontally
                    // (never vertically) instead of wrapping or overflowing the card.
                    overflowX: "auto",
                    overflowY: "hidden",
                    // Hide the native scrollbar for a clean swipeable strip — the
                    // common pattern for horizontally-scrolling mobile tab bars.
                    scrollbarWidth: "none", // Firefox
                    msOverflowStyle: "none", // old Edge/IE
                    "&::-webkit-scrollbar": { display: "none" }, // Chrome/Safari
                }}
            >
                {items.map((tab, index) => {
                    const isActive = index === activeIndex;

                    return (
                        <Box
                            key={`${tab.label}-${index}`}
                            role="tab"
                            aria-selected={isActive}
                            tabIndex={clickable ? 0 : -1}
                            onClick={() => clickable && onTabChange?.(index)}
                            onKeyDown={(e) => {
                                if (!clickable) return;
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onTabChange?.(index);
                                }
                            }}
                            sx={{
                                position: "relative",
                                flexShrink: 0,
                                pt: 0.5,
                                pb: 1.5,
                                px: 1,
                                borderRadius: "12px",
                                //color: isActive ? "#E9F9FB" : "transparent",
                                cursor: clickable ? "pointer" : "default",
                                transition: "background-color 0.2s ease",
                                "&:focus-visible": clickable
                                    ? { outline: "2px solid #00B3B5", outlineOffset: 2 }
                                    : {},
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    height: "2px",
                                    borderRadius: "12px",
                                    bgcolor: isActive ? activeColor : "transparent",
                                },
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    fontWeight: isActive ? 600 : 500,
                                    fontSize: { xs: "0.78rem", sm: "0.95rem" },
                                    color: isActive ? activeColor : inactiveColor,
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                    fontFamily: "inherit",
                                }}
                            >
                                {tab.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default TabLayout;
